import React, { useState } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Snackbar,
  TablePagination,
  Paper,
  Stack
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as HourglassIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
  OpenInNew as OpenInNewIcon,
  Visibility as VisibilityIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  TextRotationDownSharp,
} from '@mui/icons-material';
import { Close, History } from "@mui/icons-material";
import { getAccountLink, getTransactionLink } from '../utils/blockchainExplorer';
import BridgeModal from './BridgeModal';

const formatDate = (dateString) => {
  try {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (e) {
    return dateString;
  }
};

const getStatusChip = (status) => {
  const s = status && status.toLowerCase();
  if (s === 'completed')
    return <Chip icon={<CheckCircleIcon />} label="Success" size="small" color="success" sx={{ fontWeight: 600 }} />;
  if (s === 'pending')
    return <Chip icon={<HourglassIcon />} label="Pending" size="small" color="warning" sx={{ fontWeight: 600 }} />;
  if (s === 'failed')
    return <Chip icon={<CancelIcon />} label="Failed" size="small" color="error" sx={{ fontWeight: 600 }} />;
  return <Chip label={status} size="small" />;
};

const generateExplorerUrl = (chain, txId) => {
  if (!chain || !txId) return null;
  const c = chain.toLowerCase();
  if (c.includes('eth') || c === 'ethereum') return `https://etherscan.io/tx/${txId}`;
  if (c.includes('polygon')) return getTransactionLink(chain , txId);  //https://ponscaolygn.com/tx
  if (c.includes('ava') || c.includes('avalanche')) return `https://snowtrace.io/tx/${txId}`;
  if (c.includes('algo') || c.includes('algorand')) return getTransactionLink(chain , txId);;
  // fallback: try blockchair (generic) or return null
  return null;
};

function CopyButton({ text, label, onCopy }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      onCopy && onCopy(`${label} copied to clipboard`);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      onCopy && onCopy(`Failed to copy ${label}`);
    }
  };

  return (
    <Button
      size="small"
      variant="text"
      onClick={handleCopy}
      startIcon={copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
    >
      {copied ? 'Copied' : 'Copy'}
    </Button>
  );
}

function TransactionHistory({transactions}) {
  const [selectedTx, setSelectedTx] = useState(null);
  const [open, setOpen] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5); // default 5 rows per page


  const showToast = (message) => {
    setSnack({ open: true, message });
  };
  const closeToast = () => setSnack({ open: false, message: '' });

  const handleViewDetails = (tx) => {
    setSelectedTx(tx);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedTx(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Card sx={{ borderRadius: 3, bgcolor: 'background.paper', boxShadow: 3 }}>
        <CardHeader
          avatar={<HistoryIcon color="primary" />}
          title={<Typography variant="h6">Transaction History</Typography>}
        />
        <CardContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>From → To</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
               .map((tx) => (
                <TableRow key={tx.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(tx.createdAt)}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{tx.amount} FRAX</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label={tx.fromNetwork} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                      <Typography sx={{ color: 'text.secondary' }}>→</Typography>
                      <Chip label={tx.toNetwork} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                    </Box>
                  </TableCell>
                  <TableCell>{getStatusChip(tx.status)}</TableCell>
                  <TableCell align="right">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleViewDetails(tx)}
                    >
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
          component="div"
          count={transactions.length}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0); // reset to first page
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />

        </CardContent>
      </Card>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VisibilityIcon />
            <Typography variant="h6">Transaction Details</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedTx && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Overview */}
              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Amount</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{selectedTx.amount} FRAX</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Box sx={{ mt: 1 }}>{getStatusChip(selectedTx.status)}</Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">From Network</Typography>
                    <Typography variant="subtitle1" sx={{ textTransform: 'capitalize', fontWeight: 600 }}>{selectedTx.fromNetwork}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">To Network</Typography>
                    <Typography variant="subtitle1" sx={{ textTransform: 'capitalize', fontWeight: 600 }}>{selectedTx.toNetwork}</Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Date</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDate(selectedTx.createdAt)}</Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Receiver Address */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Receiver Address</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'grey.50', p: 1.5, borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>{selectedTx.receiverAddress}</Typography>
                  <CopyButton text={selectedTx.receiverAddress} label="Receiver address" onCopy={showToast} />
                </Box>
              </Box>

              {/* Error Message */}
              {selectedTx.errorMessage && (
                <Box><Typography variant="subtitle2"sx={{ fontWeight: 700, mb: 1 }} >Error Message</Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      bgcolor: 'error.light',
                      p: 1.5,
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        wordBreak: 'break-word',
                        flex: 1,
                        color: 'error.dark',
                      }}
                    >
                      {selectedTx.errorMessage}
                    </Typography>
                    <CopyButton
                      text={selectedTx.errorMessage}
                      label="Error message"
                      onCopy={showToast}
                    />
                  </Box>
                </Box>
              )}


              {/* Transaction Hashes */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Transaction Hashes</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {selectedTx.txHashes.map((h, idx) => (
                    <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{h.chain} Network</Typography>
                        <Box>
                          {generateExplorerUrl(h.chain, h.txId) ? (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<OpenInNewIcon />}
                              onClick={() => window.open(generateExplorerUrl(h.chain, h.txId), '_blank')}
                            >
                              View on Explorer
                            </Button>
                          ) : (
                            <Button size="small" variant="outlined" disabled startIcon={<OpenInNewIcon />}>View on Explorer</Button>
                          )}
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'grey.50', p: 1.5, borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>{h.txId}</Typography>
                        <CopyButton text={h.txId} label={`${h.chain} transaction hash`} onCopy={showToast} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={2000}
        onClose={closeToast}
        message={snack.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

const TransactionHistoryModal = ({
  open,
  onClose,
  transactions,
  setTransactions,
  selectedPropertyName,
  setSelectedPropertyName
}) => {
  const handleClose = () => {
    onClose();
    setTransactions([]);
    setSelectedPropertyName("");
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 600,
          fontSize: "1rem",
        }}
      >
        {/* Transaction History */} {selectedPropertyName}
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {transactions.length > 0 ? (
          <TransactionHistory transactions={transactions} />
        ) : (
          <Paper
            sx={{
              p: 6,
              textAlign: "center",
              borderRadius: 3,
              background: "linear-gradient(135deg,#f8fafc,#eef2ff)",
            }}
          >
            <History sx={{ fontSize: 40, color: "#64748b", mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Transaction History
            </Typography>
            <Typography color="text.secondary">
              Your cross-chain transfer history will appear here.
            </Typography>
          </Paper>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const BridgeTransactionDetails = ({ open, onClose, tx , getTransaction , openedBy = 'receiver'})=> {
  const [snack, setSnack] = React.useState({ open: false, message: "" });

  const showToast = (message) => setSnack({ open: true, message });
  const closeToast = () => setSnack({ open: false, message: "" });
  const [bridgeModalOpen, setBridgeModalOpen] = React.useState(false);
 
  // console.log("tx" , tx)

  if (!tx) return null;

  const isFailed = tx.status === "FAILED";
  const errorMsg = tx.metaData?.message || tx.errorMessage;
  const canCompleteTransfer =
  tx.status === "PENDING" && openedBy == 'sender' 
  &&
  tx.receiverBlockchainNetwork === "ALGORAND" 
  &&
  tx.isOptinCompleted;

  const sourceBlockchain = {'ALGORAND':'Algorand','POLYGON':'Polygon'}
  const selectedBridgeAccount = {
  blockchain: sourceBlockchain[tx.senderBlockchainNetwork],
  address: tx.sender?.dltAccount,
  maxTokens: tx.numberOfToken,
  listingId: tx.listingId,
  entityId: tx.sender?.entityId,
  entityType: tx.sender?.entityType,
  rpf: tx.rpf,
  senderAddress: tx.sender?.dltAccount,
  userId: tx.sender?.investorId,
  blockChainAssetId: tx.sender?.blockchainAssetId,
  prefilledAddress : tx.receiver?.dltAccount,
  prefilledTokens : tx.numberOfToken
};

  // console.log("selectedBridgeAccount" , selectedBridgeAccount)

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth 
      maxWidth="xl"
      PaperProps={{
          sx: (tx?.status === "FAILED" || tx?.status === "PENDING")
            ? {
                width: '720px !important',
                maxWidth: '720px !important',
                minWidth: '720px !important',
                minHeight: '70vh',
                borderRadius: '16px',
              }
            : {
                // SUCCESS — original behavior
                minWidth: "720px",
                borderRadius: '16px',
              }
        }}
       >
        {/* ---------------------- HEADER ---------------------- */}
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <VisibilityIcon />
            <Typography variant="h6">Bridge Transaction Details</Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={3}>

            {/* =======================================================
                1️⃣ TRANSFER SUMMARY SECTION 
            ======================================================= */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                bgcolor: "grey.50",
                borderRadius: 2,
                width: "100%",
              }}
            >
              <Box 
                sx={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  alignItems: "center",
                  mb: 2 
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: "primary.main", fontWeight: 600 }}
                >
                  Transfer Summary
                </Typography>

                {canCompleteTransfer && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setBridgeModalOpen(true)}
                  >
                    Complete Transfer
                  </Button>
                )}
              </Box>


              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Stack spacing={2}>
                    {/* Asset Name */}
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Asset Name
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {tx.tokenName || "N/A"}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", gap: "40px" }}>
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Project Name
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {tx.projectName || "N/A"}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Tokens
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {tx.numberOfToken} {tx.tokenName || "FRAX"}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Amount
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {tx.amount} 
                        </Typography>
                      </Box>

                      
                    </Box>

                    

                  </Stack>
                </Grid>
              </Grid>
            </Paper>

            {/* =======================================================
                2️⃣ RECEIVER INFORMATION
            ======================================================= */}
            <Paper
              elevation={0}
              sx={{ p: 3, bgcolor: "grey.50", borderRadius: 2 }}
            >
              <Typography
                variant="h6"
                sx={{ mb: 2, color: "primary.main", fontWeight: 600 }}
              >
                Receiver Information
              </Typography>

              <Stack spacing={2}>
                {/* Wallet Name */}
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Receiver DLT Wallet Name:
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {tx.receiverDltWalletname || "-"}
                  </Typography>
                </Box>

                {/* Legal Name */}
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Receiver Legal Name:
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {tx.receiverLegalName || tx.legalName || '-'}
                  </Typography>
                </Box>

                {/* DLT Account */}
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Receiver DLT Account:
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {tx.receiver?.dltAccount
                      ? "..." + tx.receiver.dltAccount.slice(-10)
                      : "-"}
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            {/* =======================================================
                3️⃣ STATUS & ERROR (IF FAILED)
            ======================================================= */}
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50" }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box sx={{ mt: 1 }}>{getStatusChip(tx.status)}</Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Date
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatDate(tx.created_at)}
                  </Typography>
                </Grid>
              </Grid>

              {/* ERROR MESSAGE */}
              {/* {isFailed && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: "error.light",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ color: "error.dark", mb: 1 }}>
                    Error
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "error.dark",
                      fontFamily: "monospace",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {errorMsg || "Unknown error"}
                  </Typography>
                </Box>
              )} */}
            </Paper>

            {/* =======================================================
                4️⃣ BLOCKCHAIN HASHES (Sender + Receiver)
            ======================================================= */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Transaction Hashes
              </Typography>

              <Box display="flex" flexDirection="column" gap={2}>

                {/* Sender TX */}
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Sender ({tx.sender?.blockChainNetwork})
                    </Typography>

                    {tx.sender?.txId ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<OpenInNewIcon />}
                        onClick={() =>
                          window.open(generateExplorerUrl(tx.sender.blockChainNetwork, tx.sender.txId))
                        }
                      >
                        {tx.sender.blockChainNetwork === "POLYGON"
                        ? "View on PolygonScan"
                        : tx.sender.blockChainNetwork === "ALGORAND"
                        ? "View on Pera Explorer"
                        : "View on Explorer"}
                      </Button>
                    ) : (
                      <Button size="small" disabled variant="outlined">
                        View on Explorer
                      </Button>
                    )}
                  </Box>

                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      bgcolor: "grey.50",
                      p: 1.5,
                      borderRadius: 1,
                    }}
                  >
                    <Typography sx={{ fontFamily: "monospace", flex: 1 }}>
                      {tx.sender?.txId || "-"}
                    </Typography>
                    {tx.sender?.txId && (
                      <CopyButton text={tx.sender.txId} label="Sender TX" onCopy={showToast} />
                    )}
                  </Box>
                </Box>

                {/* Receiver TX */}
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Receiver ({tx.receiver?.blockChainNetwork})
                    </Typography>

                    {tx.receiver?.txId ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<OpenInNewIcon />}
                        onClick={() =>
                          window.open(
                            generateExplorerUrl(tx.receiver.blockChainNetwork, tx.receiver.txId)
                          )
                        }
                      >
                        {tx.receiver.blockChainNetwork === "POLYGON"
                        ? "View on Polygonscan"
                        : tx.receiver.blockChainNetwork === "ALGORAND"
                        ? "View on Pera Explorer"
                        : "View on Explorer"}
                      </Button>
                    ) : (
                      <Button size="small" disabled variant="outlined">
                        View on Explorer
                      </Button>
                    )}
                  </Box>

                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      bgcolor: "grey.50",
                      p: 1.5,
                      borderRadius: 1,
                    }}
                  >
                    <Typography sx={{ fontFamily: "monospace", flex: 1 }}>
                      {tx.receiver?.txId || "-"}
                    </Typography>

                    {tx.receiver?.txId && (
                      <CopyButton text={tx.receiver.txId} label="Receiver TX" onCopy={showToast} />
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={2000}
        onClose={closeToast}
        message={snack.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />



      {bridgeModalOpen && (
      <BridgeModal
        isOpen={bridgeModalOpen}
        onClose={() => {
          setBridgeModalOpen(false);
          onClose();
          getTransaction();
        }}
        sourceBlockchain={selectedBridgeAccount.blockchain}
        sourceAddress={selectedBridgeAccount.address}
        maxTokens={selectedBridgeAccount.maxTokens}
        listingId={selectedBridgeAccount.listingId}
        entityId={selectedBridgeAccount.entityId}
        entityType={selectedBridgeAccount.entityType}
        rpf={selectedBridgeAccount.rpf}
        senderAddress={selectedBridgeAccount.senderAddress}
        userId={selectedBridgeAccount.userId}
        blockChainAssetId={selectedBridgeAccount.blockChainAssetId}
        isContinue = {canCompleteTransfer}
        selectedTransactionId={tx._id}
        prefilledAddress={selectedBridgeAccount.prefilledAddress}
        prefilledTokens={selectedBridgeAccount.prefilledTokens}
      />
    )}

    </>
  );
}
// export default BridgeTransactionDetails;


export default TransactionHistoryModal;
